import h from 'hyperscript';

import { addStyleClass } from '@disy/lib/add-style-class';
import { on } from '@disy/lib/event-util';
import { setupScrollShadow } from '@disy/lib/scroll-shadow';
import { keys } from '@disy/lib/type-util';
import '@disy/ui/disclosure';

import { createEmptyListContent } from 'ui/attribute-list/attribute-list-utils';
import type { CreateAttributeListGroupedByCategoryOptions } from 'ui/attribute-list/create-attribute-list';
import { createAttributeListGroupedByCategory } from 'ui/attribute-list/create-attribute-list';
import { SelectList, UNSELECTABLE_OPTION_CLASS } from 'ui/select-list/select-list';
import type { Select } from 'ui/select/select';
import { createSelectWithSearchButton } from 'ui/select/select-helper';

import { AttributeCategory } from 'cadenza/repository/attribute-category';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { AttributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';

import './attribute-list.css';
import i18n from './attribute-list.properties';

const COMPONENT_NAME = 'd-attribute-list';
const SEARCH_THRESHOLD = 5;

/**
 * Attribute representation used in the AttributeList component
 *
 * AttributeListAttribute is derived from WorkbookAttributeDto:
 * - Some properties are used as is.
 * - The 'visible' property is added.
 *
 * The component uses the AttributeTile component internally, so the AttributeTileAttribute is included in this type.
 */
export type AttributeListAttribute = Pick<
  WorkbookAttributeDto,
  'category' | 'enrichedObjecttypeId' | 'visibleInDataBrowser'
> & { visible?: boolean; selectable?: boolean } & AttributeTileAttribute;

interface AttributeListGroup {
  /** Group's attributes, will still be sorted by dimension/measure */
  attributes: AttributeListAttribute[];

  /** Any options given here will override the standard options */
  attributeTileOptions?: CreateAttributeListGroupedByCategoryOptions['attributeTileOptions'];

  /**
   * When this is present, this group will not be hidden when the attributes
   * list is empty. Instead, an info alert will be displayed with the given text.
   */
  emptyGroupText?: string;

  /** Text for group header */
  printName: string;
}

interface EnrichmentInfo {
  enrichedObjecttypeId: ObjecttypeIdString;
  enrichedObjecttypePrintName: string;
}

export type AttributeOrGroup = AttributeListAttribute | AttributeListGroup;

export function isAttributeListGroup(item: AttributeOrGroup): item is AttributeListGroup {
  return 'attributes' in item;
}

/**
 * Component that contains a list of attributes grouped by objecttype. On top there is a search
 * bar with additional option to select Measures/Dimensions.
 * Used as popup content of AttributePicker
 */
export class AttributeList extends HTMLElement {
  #elements?: AttributeOrGroup[];
  #enrichmentObjecttypeIds?: ObjecttypeIdString[];
  showAddData?: boolean;

  readonly #contentContainer = h(`.${COMPONENT_NAME}--content.d-stack-v.space-4`);

  constructor() {
    super();

    // Since the AttributeList consist of (potentially) multiple SelectLists,
    // we cannot rely on the SelectList "change" event, because it might be
    // dispatched also when navigating between the lists. Instead, we dispatch
    // the "select" event on click, Enter and Space.
    on(
      this,
      'click',
      (event) => {
        const selectList = event.delegateTarget as SelectList<undefined>;
        // the options of the select lists are attribute tiles, so we need to extract the attribute itself
        const selection = (selectList.selection[0] as AttributeTile | undefined)?.attribute;
        if (selection) {
          this.dispatchEvent(new AttributeListItemSelectedEvent(selection as AttributeListAttribute));
        }
      },
      { delegate: 'd-select-list' },
    );

    // Encapsulate the SelectList "change" event to avoid confusion with the AttributeList "select" event.
    on(this, 'change', (event) => event.stopImmediatePropagation());
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME, 'd-stack-v', 'space-2');
    this.#render();
  }

  get elements() {
    return this.#elements ?? [];
  }

  set elements(elements) {
    this.#elements = elements;
    this.#render();
  }

  get enrichmentObjecttypeIds() {
    return this.#enrichmentObjecttypeIds ?? [];
  }

  set enrichmentObjecttypeIds(value: ObjecttypeIdString[]) {
    this.#enrichmentObjecttypeIds = value;
    this.#render();
  }

  get #search() {
    return this.querySelector<Select>('d-select');
  }

  #render() {
    if (!document.contains(this)) {
      return;
    }

    this.innerHTML = '';

    if (this.#getAllAttributes().length > SEARCH_THRESHOLD) {
      this.append(h(`.${COMPONENT_NAME}--header.d-stack-v.space-3`, [this.#renderSearch()]));
    }

    this.append(this.#contentContainer);
    setupScrollShadow(this.#contentContainer);
    this.#updateContent();
  }

  #getAllAttributes(): AttributeListAttribute[] {
    return this.elements.flatMap((element) => (isAttributeListGroup(element) ? element.attributes : element));
  }

  #renderSearch() {
    const attributeCategories = [...new Set(this.#getAllAttributes().map((attr) => attr.category))];
    const select = createSelectWithSearchButton({
      data:
        (
          attributeCategories.length > 1 // allow searching by category only when there are multiple categories
        ) ?
          attributeCategories.map((category) => ({
            id: category,
            text: AttributeCategory.i18n(category, { count: 3 }),
          }))
        : undefined,
      multiple: true,
      tags: true,
    });
    select.addEventListener('change', () => this.#updateContent());
    return select;
  }

  #updateContent() {
    this.#contentContainer.replaceChildren(...this.#renderContent());
  }

  #renderContent() {
    if (!this.elements.length) {
      return [createEmptyListContent(this.showAddData)];
    }
    const content = this.#prepareAttributeListGroups().flatMap((group) => {
      const result = this.#createAttributeList(group);
      if (!result.length) {
        return [];
      }
      return h('d-disclosure.d-workbook-section', { expanded: true }, [h('summary', group.printName), ...result]);
    });
    if (!content.length) {
      return [h('d-alert', { type: 'info', size: 's' }, i18n('emptySearch'))];
    }
    return content;
  }

  #prepareAttributeListGroups() {
    const filteredElements = this.#getFilteredAttributesAndGroups();
    const filteredAttributes = filteredElements.filter((element) => !isAttributeListGroup(element));

    const groupByOt: Record<ObjecttypeIdString, AttributeListGroup> = {};
    const groupByEnrichedOt: Record<ObjecttypeIdString, AttributeListGroup> = {};
    const enrichmentToObjecttypeConnections: Record<ObjecttypeIdString, EnrichmentInfo> = {};

    function updateTargetFromAttribute(attribute: AttributeListAttribute) {
      const isEnrichment = !!attribute.enrichedObjecttypeId;
      const objecttypeId = attribute.objecttypeId!;
      const objecttypePrintName = attribute.objecttypePrintName!;
      const target = isEnrichment ? groupByEnrichedOt : groupByOt;
      if (!target[objecttypeId]) {
        target[objecttypeId] = {
          printName: objecttypePrintName,
          attributes: [],
        };
      }
      if (filteredAttributes.includes(attribute)) {
        target[objecttypeId].attributes.push(attribute);
      }
      if (isEnrichment) {
        enrichmentToObjecttypeConnections[objecttypeId] = {
          enrichedObjecttypeId: attribute.enrichedObjecttypeId!,
          enrichedObjecttypePrintName: attribute.enrichedObjecttypePrintName!,
        };
      }
    }

    const groupOrder = new Set<string>();
    for (const element of this.elements) {
      if (isAttributeListGroup(element)) {
        groupOrder.add(element.printName);
        // Flat mapping attributes from a group affects final group order. If attributes from group belong to different
        // objecttypes then grouping might affect other attributes which belong to same objecttypes. This can lead to
        // unexpected objecttype order change when elements are updated.
        element.attributes.forEach(updateTargetFromAttribute);
      } else {
        groupOrder.add(element.objecttypePrintName!);
        updateTargetFromAttribute(element);
      }
    }

    // add the enrichment attributes to the attribute list group
    for (const enrichment of keys(groupByEnrichedOt)) {
      const enrichmentInfo = enrichmentToObjecttypeConnections[enrichment];
      const enrichedObjecttypeId = enrichmentInfo.enrichedObjecttypeId;
      const enrichmentAttributes = groupByEnrichedOt[enrichment].attributes;
      const enrichedObjecttype = groupByOt[enrichedObjecttypeId];
      if (enrichedObjecttype) {
        enrichedObjecttype.attributes.push(...enrichmentAttributes);
      } else {
        // the enriched objecttype doesn't have any attributes besides the enrichment
        groupByOt[enrichedObjecttypeId] = {
          printName: enrichmentInfo.enrichedObjecttypePrintName,
          attributes: enrichmentAttributes,
        };
      }
    }
    const groupOrderMap: Record<string, number> = {};
    Array.from(groupOrder).forEach((group, index) => {
      groupOrderMap[group] = index;
    });
    return [
      ...(filteredElements.filter((el) => isAttributeListGroup(el)) as AttributeListGroup[]),
      ...Object.values(groupByOt).sort((a, b) => groupOrderMap[a.printName] - groupOrderMap[b.printName]),
    ];
  }

  #createAttributeList(attributeListGroup: AttributeListGroup) {
    const lists = createAttributeListGroupedByCategory(attributeListGroup.attributes, {
      enrichmentAttributeGroupings: this.#enrichmentObjecttypeIds?.map((enrichmentId) => ({ enrichmentId })),
      acceptedKeywords: this.#search?.selection.map((item) => item.id),
      createAttributeList: (attributeItems, labelId, { styleClass } = {}) => {
        const selectList = new SelectList<undefined>(attributeItems, { ariaLabelledBy: labelId });
        selectList.classList.add('keyboard-click');
        addStyleClass(selectList, styleClass);
        return selectList;
      },
      getAttributeById: (attrId) => this.#getAllAttributes().find((attr) => attr.id === attrId)!,
      emptyGroupText: attributeListGroup.emptyGroupText,
      attributeTileOptions: attributeListGroup.attributeTileOptions,
    });
    lists
      .flatMap((list) => [...list.querySelectorAll('.d-attribute-tile[disabled]')])
      .forEach((tile) => tile.classList.add(UNSELECTABLE_OPTION_CLASS));
    return lists;
  }

  #getFilteredAttributesAndGroups() {
    const acceptedCategories: AttributeCategory[] = [];
    const acceptedKeywords: string[] = [];
    new Set(this.#search?.selection ?? []).forEach((value) => {
      if (isAttributeCategory(value.id)) {
        acceptedCategories.push(value.id);
      } else if (value.id.length >= 2) {
        acceptedKeywords.push(value.id.trim().toLowerCase());
      }
    });

    const predicate = (attr: AttributeListAttribute) =>
      attr.visible !== false && attributeMatchesCategoryOrKeyword(attr, acceptedCategories, acceptedKeywords);

    return this.elements.flatMap((element) => {
      if (isAttributeListGroup(element)) {
        const attributes = element.attributes.filter(predicate);
        return attributes.length > 0 ? { ...element, attributes } : [];
      } else {
        return predicate(element) ? element : [];
      }
    });
  }
}

customElements.define(COMPONENT_NAME, AttributeList);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: AttributeList;
  }
}

export class AttributeListItemSelectedEvent extends CustomEvent<{ attribute: AttributeListAttribute }> {
  constructor(attribute: AttributeListAttribute) {
    super('select', { detail: { attribute } });
  }
}

function isAttributeCategory(value: string): value is AttributeCategory {
  return AttributeCategory.values.includes(value as AttributeCategory);
}

function attributeMatchesCategoryOrKeyword(
  attribute: AttributeListAttribute,
  acceptedCategories: AttributeCategory[],
  acceptedKeywords: string[],
) {
  const lowerCasePrintName = attribute.printName.toLowerCase();
  return (
    (acceptedCategories.length === 0 && acceptedKeywords.length === 0) ||
    acceptedCategories.includes(attribute.category) ||
    acceptedKeywords.some((keyword) => lowerCasePrintName.includes(keyword))
  );
}

export function createAttributeList(
  attributes: AttributeOrGroup[],
  {
    enrichmentObjecttypeIds,
    showAddData,
  }: { enrichmentObjecttypeIds?: ObjecttypeIdString[]; showAddData?: boolean } = {},
) {
  return h<AttributeList>('d-attribute-list', {
    elements: attributes,
    enrichmentObjecttypeIds,
    showAddData,
  });
}
